import React, { useMemo } from "react";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import wsgData from "./data/wsg.json";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const palette = {
  text: "#0f172a",
  muted: "#475569",
  border: "#cbd5e1",
  surface: "#ffffff",
  panel: "#f8fafc",
  blue: "#1d4ed8",    // Storage
  teal: "#0f766e",    // Supply
  amber: "#b45309",   // Demand
  red: "#b91c1c",     // Gap
  sky: "#0369a1",     // Reservoir Outlook
};

const sectionClass = "mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10";

const replacements = [
  ["â€“", "-"],
  ["â€™", "'"],
  ["â€˜", "'"],
  ["â€œ", "\""],
  ["â€\u009d", "\""],
  ["â€‘", "-"],
];

const normalizeText = (value) => {
  if (typeof value !== "string") return value;
  return replacements.reduce(
    (result, [from, to]) => result.split(from).join(to),
    value
  );
};

const getNumbers = (value) => {
  const text = normalizeText(value);
  if (typeof text !== "string") return [];
  return [...text.matchAll(/(^|[^0-9.])(-?\d+(?:\.\d+)?)/g)].map((match) => Number(match[2]));
};

const getMidpoint = (value) => {
  const numbers = getNumbers(value);
  if (!numbers.length) return null;
  if (numbers.length === 1) return numbers[0];
  return Number(((numbers[0] + numbers[1]) / 2).toFixed(2));
};

const getRangeLabel = (value) => normalizeText(value);

const compactRangeLabel = (value) => normalizeText(value)?.replace(/\s+to\s+/g, " to ");

const getBasinReservoirEstimate = (row) => {
  const midpoint = getMidpoint(row["Reservoir Outlook"]);
  if (midpoint !== null) return midpoint;

  const outlook = normalizeText(row["Reservoir Outlook"])?.toLowerCase() || "";
  if (outlook.includes("declining")) return 45;
  return null;
};

const getStatusTone = (status) => {
  const value = status.toLowerCase();
  if (value.includes("critical") || value.includes("severe")) return "bg-red-50 text-red-700 border-red-200";
  if (value.includes("deficit") || value.includes("risk") || value.includes("stress")) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-emerald-50 text-emerald-700 border-emerald-200";
};

const baselineCardStyles = {
  "Reservoir Storage": {
    color: "#2563eb",
    borderColor: "#93c5fd",
  },
  "Snowpack / Runoff": {
    color: "#0284c7",
    borderColor: "#7dd3fc",
  },
  "Lake Powell inflow": {
    color: "#0f766e",
    borderColor: "#5eead4",
  },
  "Drought Coverage": {
    color: "#b45309",
    borderColor: "#fbbf24",
  },
  "Avg inflow (GSL system)": {
    color: "#0369a1",
    borderColor: "#7dd3fc",
  },
  "Evaporation (GSL)": {
    color: "#b91c1c",
    borderColor: "#fca5a5",
  },
};

const ChartTooltip = ({ active, payload, label, title, unitLabel }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      className="min-w-[240px] rounded-xl border bg-white p-4 shadow-xl"
      style={{ borderColor: palette.border }}
      role="tooltip"
    >
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
        {title}
      </p>
      <p className="mt-1 border-b pb-2 text-lg font-bold text-slate-900">
        {label}
      </p>
      <div className="mt-3 space-y-2">
        {payload.map((item) => (
          <div key={`${item.name}-${item.dataKey}`} className="flex items-center justify-between gap-4 text-sm">
            <span className="flex items-center gap-2 font-medium text-slate-700">
              <span
                className="inline-block h-3 w-3 rounded-sm shadow-sm"
                style={{ backgroundColor: item.color || item.payload.fill }}
                aria-hidden="true"
              />
              {item.name}
            </span>
            <span className="font-bold text-slate-900">
              {typeof item.value === "number" ? `${item.value} ${unitLabel}` : item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="max-w-4xl">
    <p
      className="text-sm font-bold uppercase tracking-widest"
      style={{ color: palette.blue }}
    >
      {eyebrow}
    </p>
    <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-4 text-lg leading-relaxed text-slate-600">
        {subtitle}
      </p>
    )}
  </div>
);

const UtahWaterSecurityGauge = () => {
  const data = wsgData.Wsg;

  const baselineRows = useMemo(
    () =>
      data["Table 1: Statewide Water Balance Baseline (2025)"].map((row) => ({
        metric: normalizeText(row.Metric),
        value: normalizeText(row.Value),
        interpretation: normalizeText(row.Interpretation),
      })),
    [data]
  );

  const gaugeRows = useMemo(
    () =>
      data["Table 2: Reservoir Capacity vs Annual Demand (System-Level Gauge)"][
        "Water Security Gauge (2026)"
      ].map((row) => {
        const comp = normalizeText(row.Component);
        let fill = palette.blue;
        if (comp.includes("Supply")) fill = palette.teal;
        else if (comp.includes("Consumption") || comp.includes("Demand")) fill = palette.amber;
        else if (comp.includes("Gap")) fill = palette.red;

        return {
          component: comp,
          shortLabel: comp.replace("Annual ", ""),
          chartLabel: comp
            .replace("Total Active Reservoir Storage", "Reservoir storage")
            .replace("Annual Renewable Supply", "Renewable supply")
            .replace("Annual Consumption", "Annual use"),
          displayValue: normalizeText(row["Acre-Feet (AF)"]),
          midpointMaf: getMidpoint(row["Acre-Feet (AF)"]),
          status: normalizeText(row.Status),
          fill
        };
      }),
    [data]
  );

  const projectionRows = useMemo(
    () =>
      data["Table 3: 10-Year Projection (Olympics Scenario)"]["Projection Data"].map(
        (row) => ({
          year: normalizeText(String(row.Year)),
          supplyMaf: getMidpoint(row["Supply (MAF)"]),
          demandMaf: getMidpoint(row["Demand (MAF)"]),
          gapMaf: getMidpoint(row["Gap (MAF)"]),
          deficitMaf: Math.abs(getMidpoint(row["Gap (MAF)"]) || 0),
          supplyLabel: getRangeLabel(row["Supply (MAF)"]),
          demandLabel: getRangeLabel(row["Demand (MAF)"]),
          gapLabel: getRangeLabel(row["Gap (MAF)"]),
          reservoirLabel: getRangeLabel(row["Reservoir Level (%)"]),
          status: normalizeText(row["Security Status"]),
        })
      ),
    [data]
  );

  const basinRows = useMemo(
    () =>
      data["Table 5: Basin-Level Reservoir vs Demand (2034 Snapshot)"].map((row) => ({
        basin: normalizeText(row.Basin),
        demandGrowthPercent: getMidpoint(row["Demand Growth"]),
        reservoirMidpointPercent: getBasinReservoirEstimate(row),
        reservoirLabel: normalizeText(row["Reservoir Outlook"]),
        demandLabel: normalizeText(row["Demand Growth"]),
        status: normalizeText(row["WSI Status"]),
      })),
    [data]
  );

  const basinStressRows = useMemo(
    () =>
      basinRows
        .map((row) => ({
          ...row,
          stressScore: Math.round((100 - (row.reservoirMidpointPercent || 50)) + (row.demandGrowthPercent || 0) * 1.4),
        }))
        .sort((a, b) => b.stressScore - a.stressScore),
    [basinRows]
  );

  const lakeRows = useMemo(
    () =>
      data["Table 4: Great Salt Lake-Linked Water Security (Key Indicator)"][
        "Current & Required Balance"
      ].map((row) => ({
        metric: normalizeText(row.Metric),
        value: normalizeText(row.Value),
      })),
    [data]
  );

  const scenarioRows = useMemo(
    () =>
      data["Table 4: Great Salt Lake-Linked Water Security (Key Indicator)"][
        "Olympic-Level Scenarios (2034)"
      ].map((row) => ({
        scenario: normalizeText(row.Scenario),
        additionalWater: normalizeText(row["Additional Water"]),
        reservoirImpact: normalizeText(row["Reservoir Impact"]),
        outcome: normalizeText(row["System Outcome"]),
      })),
    [data]
  );

  const summaryCards = [
    {
      label: "Reservoir storage",
      value: baselineRows.find((row) => row.metric === "Reservoir Storage")?.value,
      note: "Share of total reservoir capacity currently filled.",
      color: "#2563eb",
      borderColor: "#93c5fd",
    },
    {
      label: "Renewable supply",
      value: gaugeRows.find((row) => row.component === "Annual Renewable Supply")?.displayValue,
      note: "Estimated yearly renewable water entering the system.",
      color: "#0f766e",
      borderColor: "#5eead4",
    },
    {
      label: "Annual demand",
      value: gaugeRows.find((row) => row.component === "Annual Consumption")?.displayValue,
      note: "Estimated yearly water use across Utah.",
      color: "#b45309",
      borderColor: "#fbbf24",
    },
    {
      label: "Structural gap",
      value: gaugeRows.find((row) => row.component === "Structural Gap")?.displayValue,
      note: "Estimated yearly shortfall between supply and demand.",
      color: "#b91c1c",
      borderColor: "#fca5a5",
    },
  ];

  return (
    <div className="bg-slate-50 text-slate-900 font-sans">
      <LandingSection
        data={{
          imageName: "WSG.webp",
          title: "Utah Water Security Gauge",
          visible: "",
        }}
      />

      <main>
        <section className={sectionClass}>
          <div className="grid gap-8 lg:grid-cols-[0.95fr_1.35fr] lg:items-start">
            <SectionHeading
              eyebrow="Overview"
              title="Utah's water balance at a glance"
              subtitle="A compact view of storage, annual supply, annual demand, and the deficit trend from the Water Security Gauge dataset."
            />

            <div>
              <div className="grid gap-3 sm:grid-cols-2">
                {summaryCards.map((card) => (
                  <div
                    key={card.label}
                    className="relative overflow-hidden rounded-lg border p-4 shadow-sm backdrop-blur-md transition-shadow hover:shadow-md"
                    style={{
                      background: "rgba(255, 255, 255, 0.68)",
                      borderColor: card.borderColor,
                      boxShadow: `0 8px 22px ${card.borderColor}45`,
                    }}
                  >
                    <span
                      className="absolute inset-x-0 top-0 h-1"
                      style={{ backgroundColor: card.color }}
                      aria-hidden="true"
                    />
                    <p
                      className="text-sm font-bold uppercase tracking-widest"
                      style={{ color: card.color }}
                    >
                      {card.label}
                    </p>
                    <p className="mt-3 text-2xl font-extrabold leading-tight text-slate-900">
                      {card.value}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">
                      {card.note}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-y bg-white" style={{ borderColor: palette.border }}>
          <div className={sectionClass}>
            <div className="mb-5">
              <p className="text-sm font-bold uppercase tracking-widest text-sky-700">
                Current Conditions
              </p>
              <h3 className="mt-1 text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">
                Statewide Water Balance Baseline
              </h3>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {baselineRows.map((row) => {
                const cardStyle = baselineCardStyles[row.metric] || {
                  color: palette.blue,
                  borderColor: palette.border,
                };

                return (
                  <article
                    key={row.metric}
                    className="relative overflow-hidden rounded-lg border p-4 shadow-sm backdrop-blur-md transition-shadow hover:shadow-md"
                    style={{
                      background: "rgba(255, 255, 255, 0.68)",
                      borderColor: cardStyle.borderColor,
                      boxShadow: `0 8px 22px ${cardStyle.borderColor}45`,
                    }}
                  >
                    <span
                      className="absolute inset-x-0 top-0 h-1"
                      style={{ backgroundColor: cardStyle.color }}
                      aria-hidden="true"
                    />
                    <p
                      className="text-xs font-bold uppercase tracking-widest"
                      style={{ color: cardStyle.color }}
                    >
                      {row.metric}
                    </p>
                    <p className="mt-3 text-xl font-extrabold leading-tight text-slate-900">
                      {row.value}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">
                      {row.interpretation}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className={sectionClass}>
          <div className="grid gap-6 xl:grid-cols-2">
            <div
              className="rounded-lg border bg-white p-5 shadow-sm"
              style={{ borderColor: palette.border }}
            >
              <SectionHeading
                eyebrow="Chart 1: 2026 Balance"
                title="Demand is about twice renewable supply"
                subtitle="The useful comparison is annual renewable supply against annual consumption; reservoir storage is a buffer, not a yearly source."
              />
            <div
              className="mt-4 h-[320px]"
              role="figure"
              aria-label="Bar chart comparing reservoir storage, renewable annual supply, annual water demand, and the annual supply gap in million acre-feet for Utah in 2026."
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={gaugeRows}
                  layout="vertical"
                  margin={{ top: 8, right: 96, left: 8, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={palette.border} opacity={0.5} />
                  <XAxis
                    type="number"
                    domain={[0, 5]}
                    tick={{ fontSize: 13, fill: palette.text, fontWeight: 500 }}
                    axisLine={false}
                    tickLine={false}
                  >
                    <Label
                      value="Volume (MAF)"
                      position="insideBottom"
                      offset={-18}
                      style={{ fill: palette.text, fontSize: 14, fontWeight: 'bold' }}
                    />
                  </XAxis>
                  <YAxis
                    dataKey="chartLabel"
                    type="category"
                    width={132}
                    tick={{ fontSize: 12, fill: palette.text, fontWeight: 700 }}
                    axisLine={{ stroke: palette.border, strokeWidth: 2 }}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: palette.panel }}
                    content={
                      <ChartTooltip
                        title="2026 System Balance"
                        unitLabel="MAF"
                      />
                    }
                  />
                  <Bar
                    dataKey="midpointMaf"
                    name="Water Volume"
                    radius={[0, 6, 6, 0]}
                    maxBarSize={34}
                  >
                    <LabelList
                      dataKey="midpointMaf"
                      position="right"
                      formatter={(value) => `${value} MAF`}
                      className="fill-slate-700 text-xs font-bold"
                    />
                    {gaugeRows.map((row) => (
                      <Cell key={row.component} fill={row.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Descriptive legend since bars have different colors but one datakey */}
            <div className="mt-4 grid gap-3 border-t pt-4 sm:grid-cols-2" style={{ borderColor: palette.border }}>
               {gaugeRows.map((row) => (
                  <div key={row.component} className="flex items-center justify-between gap-3 rounded-md bg-slate-50 px-3 py-2 text-sm font-medium">
                    <span className="flex items-center gap-2">
                     <span className="h-4 w-4 rounded-sm shadow-sm" style={{ backgroundColor: row.fill }}></span>
                     <span className="text-slate-700">{row.component}</span>
                    </span>
                    <span className="font-bold text-slate-900">{row.displayValue}</span>
                  </div>
               ))}
            </div>
            </div>

            <div
              className="rounded-lg border bg-white p-5 shadow-sm"
              style={{ borderColor: palette.border }}
            >
              <SectionHeading
                eyebrow="Chart 2: 10-Year Outlook"
                title="The deficit widens toward 2034"
                subtitle="Supply stays nearly flat while demand rises, making the projected shortfall the signal to watch."
              />
              <div
                className="mt-4 h-[320px]"
                role="figure"
                aria-label="Line chart showing projected annual water supply, annual water demand, and the annual supply gap in Utah from 2026 to 2034."
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={projectionRows}
                    margin={{ top: 10, right: 10, left: 0, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={palette.border} opacity={0.5} />
                    <XAxis
                      dataKey="year"
                      tick={{ fontSize: 13, fill: palette.text, fontWeight: 500 }}
                      axisLine={{ stroke: palette.border, strokeWidth: 2 }}
                      tickLine={false}
                      dy={10}
                    >
                      <Label
                      value="Year"
                      position="insideBottom"
                        offset={-8}
                        style={{ fill: palette.text, fontSize: 14, fontWeight: 'bold' }}
                      />
                    </XAxis>
                    <YAxis
                      tick={{ fontSize: 13, fill: palette.text, fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                      dx={-10}
                    >
                      <Label
                      value="Volume (MAF)"
                      angle={-90}
                      position="insideLeft"
                        offset={8}
                        style={{ fill: palette.text, fontSize: 14, fontWeight: 'bold' }}
                      />
                    </YAxis>
                    <ReferenceLine y={0} stroke={palette.text} strokeDasharray="4 4" strokeWidth={2} opacity={0.4} />
                    <Tooltip
                      content={
                        <ChartTooltip
                          title="Yearly Projection"
                          unitLabel="MAF"
                        />
                      }
                    />
                    <Legend 
                      verticalAlign="top" 
                      height={42} 
                      iconType="plainline"
                      iconSize={24}
                      wrapperStyle={{ fontWeight: 600, fontSize: '14px', color: palette.text }}
                    />
                    <Area
                      type="monotone"
                      dataKey="deficitMaf"
                      name="Deficit Size"
                      fill={palette.red}
                      fillOpacity={0.12}
                      stroke={palette.red}
                      strokeOpacity={0.35}
                    />
                    <Line
                      type="monotone"
                      dataKey="supplyMaf"
                      name="Annual Supply"
                      stroke={palette.teal}
                      strokeWidth={4}
                      dot={{ r: 5, strokeWidth: 2, fill: palette.surface }}
                      activeDot={{ r: 8, strokeWidth: 0, fill: palette.teal }}
                    />
                    <Line
                      type="monotone"
                      dataKey="demandMaf"
                      name="Annual Demand"
                      stroke={palette.amber}
                      strokeWidth={4}
                      dot={{ r: 5, strokeWidth: 2, fill: palette.surface }}
                      activeDot={{ r: 8, strokeWidth: 0, fill: palette.amber }}
                    />
                    <Line
                      type="monotone"
                      dataKey="gapMaf"
                      name="Supply Gap"
                      stroke={palette.red}
                      strokeWidth={4}
                      dot={{ r: 5, strokeWidth: 2, fill: palette.surface }}
                      activeDot={{ r: 8, strokeWidth: 0, fill: palette.red }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4 overflow-hidden rounded-lg border" style={{ borderColor: palette.border }}>
                <table className="w-full table-fixed divide-y divide-slate-200">
                  <colgroup>
                    <col className="w-[25%]" />
                    <col className="w-[17%]" />
                    <col className="w-[17%]" />
                    <col className="w-[17%]" />
                    <col className="w-[24%]" />
                  </colgroup>
                  <thead className="bg-slate-50">
                    <tr>
                      {["Year", "Supply (MAF)", "Demand (MAF)", "Gap (MAF)", "Status"].map((heading) => (
                        <th
                          key={heading}
                          className="px-2 py-3 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500 sm:px-3 sm:text-xs"
                          scope="col"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {projectionRows.map((row) => (
                      <tr key={row.year} className="hover:bg-slate-50 transition-colors">
                        <td className="break-words px-2 py-3 text-xs font-bold leading-snug text-slate-900 sm:px-3 sm:text-sm">
                          {row.year}
                        </td>
                        <td className="px-2 py-3 text-xs leading-snug text-slate-600 sm:px-3 sm:text-sm">
                          {compactRangeLabel(row.supplyLabel)}
                        </td>
                        <td className="px-2 py-3 text-xs leading-snug text-slate-600 sm:px-3 sm:text-sm">
                          {compactRangeLabel(row.demandLabel)}
                        </td>
                        <td className="px-2 py-3 text-xs font-bold leading-snug text-red-700 sm:px-3 sm:text-sm">
                          {compactRangeLabel(row.gapLabel)}
                        </td>
                        <td className="px-2 py-3 text-xs font-medium text-slate-800 sm:px-3">
                          <span className={`inline-flex max-w-full rounded-full border px-2 py-1 text-[10px] font-bold leading-tight sm:text-xs ${getStatusTone(row.status)}`}>
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y bg-white" style={{ borderColor: palette.border }}>
          <div className={`${sectionClass} grid gap-6 xl:grid-cols-[1.35fr_0.95fr]`}>
            <div
              className="rounded-lg border bg-white p-5 shadow-sm"
              style={{ borderColor: palette.border }}
            >
              <SectionHeading
                eyebrow="Chart 3: Basin Stress"
                title="Southwest and Sevier basins carry the sharpest risk"
                subtitle="Stress score combines lower reservoir outlook with higher demand growth, so the chart ranks pressure rather than mixing two separate measures."
              />
            <div
              className="mt-4 h-[330px]"
              role="figure"
              aria-label="Horizontal bar chart ranking Utah basins by derived water stress score for 2034."
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={basinStressRows}
                  layout="vertical"
                  margin={{ top: 8, right: 20, left: 10, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={palette.border} opacity={0.5} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12, fill: palette.text, fontWeight: 500 }}
                    axisLine={false}
                    tickLine={false}
                  >
                    <Label
                      value="Derived Stress Score"
                      position="insideBottom"
                      offset={-4}
                      style={{ fill: palette.text, fontSize: 13, fontWeight: 'bold' }}
                    />
                  </XAxis>
                  <YAxis
                    dataKey="basin"
                    type="category"
                    width={135}
                    tick={{ fontSize: 12, fill: palette.text, fontWeight: 700 }}
                    axisLine={{ stroke: palette.border, strokeWidth: 2 }}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: palette.panel }}
                    content={
                      <ChartTooltip
                        title="2034 Basin Outlook"
                        unitLabel="pts"
                      />
                    }
                  />
                  <Bar
                    dataKey="stressScore"
                    name="Stress Score"
                    radius={[0, 6, 6, 0]}
                    maxBarSize={34}
                  >
                    {basinStressRows.map((row) => (
                      <Cell
                        key={row.basin}
                        fill={row.stressScore >= 90 ? palette.red : row.stressScore >= 70 ? palette.amber : palette.teal}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {basinStressRows.map((row) => (
                  <div key={row.basin} className="rounded-md bg-slate-50 p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-bold text-slate-900">{row.basin}</span>
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${getStatusTone(row.status)}`}>
                        {row.status}
                      </span>
                    </div>
                    <p className="mt-2 text-slate-600">
                      Reservoir: {row.reservoirLabel}; demand growth: {row.demandLabel}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-slate-800 p-5 text-white shadow-sm">
              <p className="text-sm font-bold uppercase tracking-widest text-slate-300">
                Great Salt Lake
              </p>
              <h2 className="mt-2 text-2xl font-extrabold tracking-tight">
                System-level impact
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                The lake indicator shows how much additional water is needed to move from persistent deficit toward stabilization.
              </p>

              <div className="mt-5 grid gap-3">
                {lakeRows.map((row) => (
                  <div
                    key={row.metric}
                    className="rounded-lg border border-slate-600 bg-slate-700/50 p-4"
                  >
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                      {row.metric}
                    </p>
                    <p className="mt-2 text-2xl font-extrabold text-white">
                      {row.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-5 space-y-3">
                {scenarioRows.map((row) => (
                  <article
                    key={row.scenario}
                    className="rounded-lg border border-slate-600 bg-slate-700/50 p-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-bold text-white">
                        {row.scenario}
                      </p>
                      <p className="text-sm font-extrabold text-emerald-300">
                        {row.additionalWater}
                      </p>
                    </div>
                    <p className="mt-2 text-sm text-slate-300">
                      {row.outcome} - {row.reservoirImpact}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className={sectionClass}>
          <SectionHeading
            eyebrow="Documentation"
            title="Sources & Notes"
            subtitle="References and context from the Water Security Gauge dataset."
          />

          <div className="mt-8 flex flex-wrap gap-4">
            {data.Sources.map((source) => (
              <a
                key={source}
                href={source}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-full bg-slate-100 px-6 py-3 text-sm font-bold text-blue-700 hover:bg-blue-50 hover:text-blue-800 transition-colors"
              >
                {source}
                <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            ))}
          </div>

          <div className="mt-8 max-w-4xl rounded-lg border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
            <p className="font-bold text-slate-900">
              {normalizeText(data["WSI Definition"])}
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
            {data.Notes?.map((note, idx) => (
              <p key={`note-${idx}`} className="flex items-start">
                <span className="mr-3 mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400"></span>
                <span>{normalizeText(note)}</span>
              </p>
            ))}
            {data["Table 4: Great Salt Lake-Linked Water Security (Key Indicator)"].Notes?.map(
              (note, idx) => (
                <p key={`lake-note-${idx}`} className="flex items-start">
                  <span className="mr-3 mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400"></span>
                  <span>{normalizeText(note)}</span>
                </p>
              )
            )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default UtahWaterSecurityGauge;
